/**
 * Entity Hierarchy Tree Builder
 *
 * Builds account-based hierarchy trees for different entity types.
 * Used for tree selection components in widget configuration.
 */
import type { AccountEntity } from '@/types/entities'
import { ENTITIES_CONFIG, EntityConfig } from '@/enum/etitiesConfig'

/* Constants */
export const ACCOUNT_NODE_TYPE = 'account'
export const ENTITY_NODE_TYPE = 'entity'
/* Types */
export interface EntityTreeNode {
    nodeKey: string | number
    label: string
    type: typeof ACCOUNT_NODE_TYPE | typeof ENTITY_NODE_TYPE
    children: EntityTreeNode[]
    isLeaf: boolean
    entityType?: string
    icon?: string
    /** Original entity data for additional context */
    data?: unknown
}

export interface TreeBuildResult {
    tree: EntityTreeNode[]
}

export type EntityTreeConfig<T = unknown> = EntityConfig<T> & {
    accountIdKey?: keyof T
}

/**
 * Builds a hierarchical tree from accounts and entities.
 *
 * @param accounts - List of accounts (used as parent nodes)
 * @param entities - List of entities to be placed under accounts
 * @param entityConfig - Configuration for the entity type
 * @returns Tree structure with accounts as parents and entities as children
 */

export function buildEntityHierarchyTree<T> (
    accounts: AccountEntity[],
    entities: T[],
    entityConfig: EntityTreeConfig<T>
): TreeBuildResult {
    const accountMap = new Map<number, EntityTreeNode>()
    const roots: EntityTreeNode[] = []

    /* ===================== ACCOUNTS ===================== */

    for (const account of accounts) {
        accountMap.set(account.AccountID, {
            nodeKey: `account-${account.AccountID}`,
            label: account.dist_name ?? String(account.AccountID),
            type: ACCOUNT_NODE_TYPE,
            children: [],
            isLeaf: false,
            data: account,
        })
    }

    for (const node of accountMap.values()) {
        const account = node.data as AccountEntity
        const parentId = account.ParentAccountID

        if (parentId && accountMap.has(parentId)) {
            accountMap.get(parentId)?.children.push(node)
        } else {
            roots.push(node)
        }
    }

    if (entityConfig.idKey === ENTITIES_CONFIG.Accounts.idKey) {
        return {
            tree: roots,
        }
    }
    /* ===================== ENTITIES ===================== */

    // Skip entity processing if no accountIdKey (means we're selecting accounts)
    if (!entityConfig.accountIdKey) {
        return {
            tree: roots,
        }
    }

    for (const entity of entities) {
        const accountId = entity[entityConfig.accountIdKey]
        if (typeof accountId !== 'number') continue

        const parentAccount = accountMap.get(accountId)
        if (!parentAccount) continue

        const entityId = entity[entityConfig.idKey]
        const entityLabel = entity[entityConfig.labelKey]

        parentAccount.children.push({
            nodeKey: +entityId,
            label: entityLabel != null ? String(entityLabel) : String(entityId),
            type: ENTITY_NODE_TYPE,
            children: [],
            isLeaf: true,
            icon: entityConfig.icon,
            data: entity,
        })
    }

    return {
        tree: filterNodesWithEntities(roots),
    }
}

/**
 * Checks if a node has any entities in its subtree
 */
function hasEntitiesInSubtree (node: EntityTreeNode): boolean {
    if (node.type === ENTITY_NODE_TYPE) return true
    if (!node.children?.length) return false
    return node.children.some(child => hasEntitiesInSubtree(child))
}

/**
 * Recursively filters tree to only include accounts that have entities in their subtree
 */
function filterNodesWithEntities (nodes: EntityTreeNode[]): EntityTreeNode[] {
    return nodes
        .filter(node => hasEntitiesInSubtree(node))
        .map(node => {
            if (node.type === ENTITY_NODE_TYPE || !node.children?.length) {
                return node
            }
            return {
                ...node,
                children: filterNodesWithEntities(node.children),
            }
        })
}

/**
 * Finds a node by key in the tree
 */
export function findNodeByKey (
    nodes: EntityTreeNode[],
    nodeKey: string | number
): EntityTreeNode | null {
    for (const node of nodes) {
        if (node.nodeKey === nodeKey) {
            return node
        }
        if (node.children?.length) {
            const found = findNodeByKey(node.children, nodeKey)
            if (found) return found
        }
    }
    return null
}

/**
 * Gets all child nodes of a specific type from a parent node
 */
export function getChildNodesByType (
    parentNode: EntityTreeNode,
    type: typeof ACCOUNT_NODE_TYPE | typeof ENTITY_NODE_TYPE
): EntityTreeNode[] {
    const result: EntityTreeNode[] = []
    traverseByType(parentNode, type, result)
    return result
}

function traverseByType (
    node: EntityTreeNode,
    type: string,
    result: EntityTreeNode[]
): void {
    if (node.type === type) result.push(node)
    node.children?.forEach(child => traverseByType(child, type, result))
}

/**
 * Extracts all entity keys from a tree
 */
export function getAllEntityKeys (tree: EntityTreeNode[]): (string | number)[] {
    const keys: (string | number)[] = []

    function traverse (nodes: EntityTreeNode[]) {
        for (const node of nodes) {
            if (node.type === ENTITY_NODE_TYPE) {
                keys.push(node.nodeKey)
            }
            if (node.children?.length) {
                traverse(node.children)
            }
        }
    }

    traverse(tree)
    return keys
}

